import { useState, useEffect } from 'react';
import { tenant as tenantApi, leads as leadsApi } from '../services/api';

const TIPO_LABEL = { ALQUILER: 'Alquiler', COMPRA: 'Compra', VENTA: 'Venta', ARRENDAR: 'Arrendar' };
const TIPO_COLOR = { ALQUILER: 'var(--cyan)', COMPRA: 'var(--green)', VENTA: 'var(--amber)', ARRENDAR: 'var(--text2)' };

export default function Overview() {
  const [data, setData] = useState(null);
  const [tokens, setTokens] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([tenantApi.getMe(), tenantApi.getTokenUsage(), leadsApi.getStats()])
      .then(([me, tok, st]) => {
        setData(me);
        setTokens(tok);
        setStats(st);
        setLoading(false);
      });
  }, []);

  if (loading) return <div className="content" style={{color:'var(--text3)'}}>Cargando...</div>;

  const pct = tokens?.mes_actual?.porcentaje || 0;
  const tokensUsados = tokens?.mes_actual?.tokens_usados || 0;

  return (
    <div>
      <div className="topbar">
        <div>
          <div className="page-title">Overview</div>
          <div className="page-sub">Resumen de tu agente IA</div>
        </div>
        <div className="topbar-actions">
          <div className="status-label">
            <span className="status-dot"></span>
            {data?.activo ? 'Agente online' : 'Agente inactivo'}
          </div>
        </div>
      </div>

      <div className="content">
        <div className="metrics">
          <div className="metric metric-green">
            <div className="metric-label">Plan activo</div>
            <div className="metric-value" style={{fontSize:'18px'}}>{data?.plan || 'basic'}</div>
            <div className="metric-sub">{data?.activo ? 'activo' : 'inactivo'}</div>
          </div>
          <div className="metric metric-cyan">
            <div className="metric-label">Tokens Groq</div>
            <div className="metric-value">{Math.round(tokensUsados/1000)}k</div>
            <div className="metric-sub">este mes</div>
          </div>
          <div className="metric">
            <div className="metric-label">WhatsApp</div>
            <div className="metric-value" style={{fontSize:'14px', marginTop:'4px'}}>
              {data?.whatsapp_configurado ? '✓' : '✗'}
            </div>
            <div className="metric-sub">{data?.whatsapp_configurado ? 'configurado' : 'pendiente'}</div>
          </div>
          <div className="metric">
            <div className="metric-label">Groq API</div>
            <div className="metric-value" style={{fontSize:'14px', marginTop:'4px'}}>
              {data?.groq_configurado ? '✓' : '✗'}
            </div>
            <div className="metric-sub">{data?.groq_configurado ? 'configurado' : 'usando default'}</div>
          </div>
        </div>

        <div className="token-bar-wrap card">
          <div className="token-header">
            <div className="token-label">Uso de tokens este mes</div>
            <div className="token-count">
              <span>{tokensUsados.toLocaleString()}</span> / {(tokens?.tokens_limite || 5000000).toLocaleString()} tokens
            </div>
          </div>
          <div className="bar-track">
            <div className={`bar-fill ${pct > 80 ? 'warn' : ''}`} style={{width: `${Math.min(pct,100)}%`}}></div>
          </div>
          <div className="bar-legend"><span>0</span><span>{Math.round((tokens?.tokens_limite || 5000000)/2000)}k</span><span>{Math.round((tokens?.tokens_limite || 5000000)/1000)}k</span></div>
          {pct > 80 && (
            <div style={{marginTop:'10px', fontSize:'11px', color:'var(--amber)'}}>
              ⚠ Llevas el {pct}% del plan. Considera ampliar el límite.
            </div>
          )}
        </div>

        <div className="divider"></div>

        {/* ── Leads ── */}
        <div className="metrics">
          <div className="metric metric-green">
            <div className="metric-label">Total leads</div>
            <div className="metric-value">{stats?.total ?? '—'}</div>
            <div className="metric-sub">acumulados</div>
          </div>
          <div className="metric metric-cyan">
            <div className="metric-label">Últimos 7 días</div>
            <div className="metric-value">{stats?.nuevos_7d ?? '—'}</div>
            <div className="metric-sub">leads nuevos</div>
          </div>
          <div className="metric">
            <div className="metric-label">Pendientes</div>
            <div className="metric-value">{stats?.por_estado?.nuevo ?? 0}</div>
            <div className="metric-sub">sin gestionar</div>
          </div>
          <div className="metric">
            <div className="metric-label">Cerrados</div>
            <div className="metric-value">{stats?.por_estado?.cerrado ?? 0}</div>
            <div className="metric-sub">completados</div>
          </div>
        </div>

        <div className="two-col" style={{marginTop:'16px'}}>
          <div className="card">
            <div className="card-title">Leads por tipo</div>
            {stats?.total > 0 ? (
              Object.entries(TIPO_LABEL).map(([key, label]) => {
                const n = stats?.por_tipo?.[key] || 0;
                const pct = stats?.total ? Math.round((n / stats.total) * 100) : 0;
                return (
                  <div key={key} style={{marginBottom:'10px'}}>
                    <div style={{display:'flex', justifyContent:'space-between', fontSize:'11px', marginBottom:'4px'}}>
                      <span style={{color:'var(--text2)'}}>{label}</span>
                      <span style={{color:'var(--text3)'}}>{n}</span>
                    </div>
                    <div className="bar-track" style={{height:'6px'}}>
                      <div style={{width:`${pct}%`, height:'100%', background: TIPO_COLOR[key], borderRadius:'3px', transition:'width .3s'}}></div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{color:'var(--text3)', fontSize:'11px'}}>Sin leads aún</div>
            )}
          </div>
          <div className="card">
            <div className="card-title">Estado del pipeline</div>
            {[
              ['nuevo',      'Nuevos',      'var(--cyan)'],
              ['contactado', 'Contactados', 'var(--green)'],
              ['visita',     'Visita',      'var(--amber)'],
              ['cerrado',    'Cerrados',    'var(--text2)'],
              ['descartado', 'Descartados', 'var(--red, #e05)'],
            ].map(([key, label, color]) => (
              <div key={key} className="toggle-wrap" style={{padding:'6px 0'}}>
                <div className="toggle-info">
                  <div className="toggle-name" style={{fontSize:'12px'}}>{label}</div>
                </div>
                <div style={{fontWeight:600, color, fontSize:'16px'}}>{stats?.por_estado?.[key] ?? 0}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="divider"></div>

        <div className="two-col">
          <div className="card">
            <div className="card-title">Consumo últimos 7 días</div>
            <div className="token-chart">
              {tokens?.ultimos_7_dias?.length > 0 ? (
                [...tokens.ultimos_7_dias].reverse().map((d, i) => {
                  const max = Math.max(...tokens.ultimos_7_dias.map(x => x.tokens));
                  const h = Math.round((d.tokens / max) * 100);
                  return (
                    <div
                      key={i}
                      className={`t-bar ${d.tokens > 10000 ? 'warn' : 'hi'}`}
                      style={{height: `${h}%`}}
                      title={`${d.dia}: ${d.tokens.toLocaleString()} tokens`}
                    />
                  );
                })
              ) : (
                <div style={{color:'var(--text3)', fontSize:'11px'}}>Sin datos aún</div>
              )}
            </div>
          </div>
          <div className="card">
            <div className="card-title">Integraciones</div>
            <div className="toggle-wrap">
              <div className="toggle-info">
                <div className="toggle-name">WhatsApp</div>
                <div className="toggle-desc">{data?.whatsapp_configurado ? 'Configurado' : 'Pendiente de configurar'}</div>
              </div>
              <label className="toggle">
                <input type="checkbox" readOnly checked={data?.whatsapp_configurado || false}/>
                <span className="slider-toggle"></span>
              </label>
            </div>
            <div className="toggle-wrap">
              <div className="toggle-info">
                <div className="toggle-name">Web widget</div>
                <div className="toggle-desc">Embed en tu web</div>
              </div>
              <label className="toggle">
                <input type="checkbox" readOnly checked={true}/>
                <span className="slider-toggle"></span>
              </label>
            </div>
            <div className="toggle-wrap">
              <div className="toggle-info">
                <div className="toggle-name">Google Calendar</div>
                <div className="toggle-desc">Citas automáticas</div>
              </div>
              <label className="toggle">
                <input type="checkbox" readOnly checked={data?.google_calendar || false}/>
                <span className="slider-toggle"></span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}